import { PLATFORM_LIMITS, hashtagCount, isXThread, platformTextLength, xWeightedLength } from '@unipostudio/platform-constants';

/** The platform's id in the web's icon set: its API name in lower case. */
export type RulePlatformId =
  | 'instagram'
  | 'facebook'
  | 'twitter'
  | 'linkedin'
  | 'whatsapp'
  | 'telegram'
  | 'youtube'
  | 'pinterest'
  | 'tiktok'
  | 'google_business'
  | 'zetpin';

/**
 * Single source of truth for what each platform (and each post TYPE within it)
 * actually accepts. The composer, the API and the worker use this for three things that used to be
 * guesswork: which post types exist, what media a type REQUIRES, and how the
 * caption is counted/truncated for that platform.
 *
 * Keeping it in one module means the selector, the live preview and the publish
 * gate can never disagree about, say, "a Reel needs a video".
 */

export type MediaNeed =
  | 'none'      // text-only is fine
  | 'image'     // needs at least one image
  | 'video'     // needs a video
  | 'media'     // image or video, at least one
  | 'carousel'; // needs 2+ items

export interface PostTypeRule {
  id: string;
  /** English name, for logs and tests. The UI shows `t(labelKey)`. */
  label: string;
  /** i18n key of the type's name (`composer.rules.type.*`). */
  labelKey: string;
  /** What media this type requires to be publishable. */
  needs: MediaNeed;
  /** Frame shape used by the live preview. */
  frame: 'square' | 'portrait' | 'landscape' | 'auto';
  /** i18n key of the short note shown in the selector (`composer.rules.note.*`). */
  noteKey?: string;
}

export interface PlatformRule {
  platformId: RulePlatformId;
  /** API enum name (INSTAGRAM, …) — the key used across the store + API. */
  name: string;
  label: string;
  /** Caption limit. YouTube/Pinterest count their description field. */
  chars: number;
  /** Max hashtags before the platform penalises or rejects. null = no cap. */
  hashtags: number | null;
  /** LinkedIn de-ranks >5 hashtags — warn rather than block. */
  hashtagsAreSoftCap?: boolean;
  /** Links are not tappable in-caption (Instagram) → we append link-in-bio. */
  linksClickable: boolean;
  /** X counts every URL as exactly 23 characters. */
  urlWeight?: number;
  /** Where the platform folds long copy in its own UI. */
  foldAt?: number;
  /** Every pin needs a destination URL; without one in the caption the post is blocked. */
  requiresLink?: boolean;
  /** A conversation channel only: it lives in the inbox and nothing publishes posts to it. */
  inboxOnly?: boolean;
  types: PostTypeRule[];
}

const LINK_RE = /https?:\/\/[^\s]+/i;

/** X: up to 4 images, or 1 video, in one post; never both. */
const X_MAX_IMAGES = 4;
/** TikTok: a photo post takes up to 35 stills. */
const TIKTOK_MAX_PHOTOS = 35;
/** Zetpin: up to 10 images in one post, and no video (the Developer API takes images). */
const ZETPIN_MAX_IMAGES = PLATFORM_LIMITS.ZETPIN.maxImages;
/** An Instagram carousel: 2 to 20 items (PLATFORM_LIMITS). */
const INSTAGRAM_CAROUSEL_MAX = PLATFORM_LIMITS.INSTAGRAM.mediaSpecs.carousel.maxItems;
/** Instagram refuses a caption that tags more than 20 accounts. */
const INSTAGRAM_MAX_MENTIONS = PLATFORM_LIMITS.INSTAGRAM.mentions;
/** LinkedIn: one video, or up to 20 images, in one post (never both). */
const LINKEDIN_MAX_IMAGES = 20;
/** A TikTok photo post's description is longer than a video's caption. */
const TIKTOK_PHOTO_DESC_MAX = PLATFORM_LIMITS.TIKTOK.photoDescChars;

/** An @handle that tags an account: not part of an e-mail address or a word. */
const MENTION_RE = /(^|[^\p{L}\p{N}_@.])@[\p{L}\p{N}_.]*[\p{L}\p{N}_]/gu;

/** How many accounts a caption tags (@handle), as Instagram counts them. */
export function countMentions(text: string): number {
  return [...text.matchAll(MENTION_RE)].length;
}
/** Channels whose post carries a single photo or video. */
const SINGLE_MEDIA_PLATFORMS: ReadonlySet<string> = new Set(['PINTEREST', 'GOOGLE_BUSINESS', 'YOUTUBE']);

/** Whether the caption carries a link the platform can use as the destination. */
export function hasLink(caption: string): boolean {
  return LINK_RE.test(caption);
}

/** A post type; its name's i18n key is `composer.rules.type.<label in lower case>`, its note `composer.rules.note.<note>`. */
const T = (id: string, label: string, needs: MediaNeed, frame: PostTypeRule['frame'], note?: string): PostTypeRule =>
  ({ id, label, labelKey: `composer.rules.type.${label.toLowerCase()}`, needs, frame, ...(note ? { noteKey: `composer.rules.note.${note}` } : {}) });

export const PLATFORM_RULES: PlatformRule[] = [
  {
    platformId: 'instagram',
    name: 'INSTAGRAM',
    label: 'Instagram',
    chars: PLATFORM_LIMITS.INSTAGRAM.chars,
    hashtags: PLATFORM_LIMITS.INSTAGRAM.hashtags,
    linksClickable: false,
    foldAt: 125,
    types: [
      T('Post', 'Post', 'image', 'square', 'needsImage'),
      T('Story', 'Story', 'media', 'portrait', 'story'),
      T('Reel', 'Reel', 'video', 'portrait', 'needsVideo'),
      T('Carousel', 'Carousel', 'carousel', 'square', 'carousel'),
    ],
  },
  {
    platformId: 'linkedin',
    name: 'LINKEDIN',
    label: 'LinkedIn',
    chars: PLATFORM_LIMITS.LINKEDIN.chars,
    hashtags: PLATFORM_LIMITS.LINKEDIN.hashtags,
    hashtagsAreSoftCap: true,
    linksClickable: true,
    foldAt: 140,
    types: [
      T('Post', 'Post', 'none', 'landscape'),
      T('Article', 'Article', 'none', 'landscape', 'longForm'),
      T('Poll', 'Poll', 'none', 'landscape'),
    ],
  },
  {
    platformId: 'twitter',
    name: 'TWITTER',
    label: 'X (Twitter)',
    chars: PLATFORM_LIMITS.TWITTER.chars,
    hashtags: null,
    linksClickable: true,
    urlWeight: PLATFORM_LIMITS.TWITTER.linkCounts,
    types: [
      T('Tweet', 'Post', 'none', 'landscape'),
      T('Thread', 'Thread', 'none', 'landscape', 'autoSplit'),
    ],
  },
  {
    platformId: 'facebook',
    name: 'FACEBOOK',
    label: 'Facebook',
    chars: PLATFORM_LIMITS.FACEBOOK.chars,
    hashtags: null,
    linksClickable: true,
    foldAt: 280,
    types: [
      T('Post', 'Post', 'none', 'landscape'),
      T('Story', 'Story', 'media', 'portrait', 'story'),
      T('Reel', 'Reel', 'video', 'portrait', 'needsVideo'),
    ],
  },
  {
    platformId: 'tiktok',
    name: 'TIKTOK',
    label: 'TikTok',
    chars: PLATFORM_LIMITS.TIKTOK.chars,
    hashtags: PLATFORM_LIMITS.TIKTOK.hashtags,
    // A caption link is plain text on TikTok. Only the profile bio carries a
    // tappable one, so the composer offers link-in-bio here as it does for
    // Instagram.
    linksClickable: false,
    types: [
      // TikTok has no text-only post: both kinds need media, and the API
      // treats them as separate endpoints rather than one post with
      // attachments. A photo post is a carousel of stills.
      T('Video', 'Video', 'video', 'portrait', 'tiktokVideo'),
      T('Photo', 'Photo', 'image', 'portrait', 'tiktokPhoto'),
    ],
  },
  {
    platformId: 'whatsapp',
    name: 'WHATSAPP',
    label: 'WhatsApp',
    chars: PLATFORM_LIMITS.WHATSAPP.chars,
    hashtags: 0,
    linksClickable: true,
    // WhatsApp only lets a business message people who wrote first, or send
    // pre-approved templates to opted-in contacts. There is no feed to post
    // to, and the publisher has no WhatsApp branch, so it is an inbox channel.
    inboxOnly: true,
    types: [T('Template', 'Template', 'none', 'auto', 'approvedTemplate')],
  },
  {
    platformId: 'telegram',
    name: 'TELEGRAM',
    label: 'Telegram',
    chars: PLATFORM_LIMITS.TELEGRAM.chars,
    hashtags: null,
    linksClickable: true,
    types: [
      T('Channel', 'Channel', 'none', 'landscape'),
      T('Group', 'Group', 'none', 'landscape'),
    ],
  },
  {
    platformId: 'youtube',
    name: 'YOUTUBE',
    label: 'YouTube',
    chars: PLATFORM_LIMITS.YOUTUBE.descChars,
    hashtags: null,
    linksClickable: true,
    types: [
      T('Video', 'Video', 'video', 'landscape', 'needsVideo'),
      T('Short', 'Short', 'video', 'portrait', 'short'),
    ],
  },
  {
    platformId: 'pinterest',
    name: 'PINTEREST',
    requiresLink: true,
    label: 'Pinterest',
    chars: PLATFORM_LIMITS.PINTEREST.descChars,
    hashtags: null,
    linksClickable: true,
    types: [T('Pin', 'Pin', 'image', 'portrait', 'needsImage')],
  },
  {
    platformId: 'google_business',
    name: 'GOOGLE_BUSINESS',
    label: 'Google Business',
    chars: PLATFORM_LIMITS.GOOGLE_BUSINESS.chars,
    // Hashtags are not links on Google, but they do no harm either.
    hashtags: null,
    // Google shows a caption link as text; the post's button is the link.
    linksClickable: false,
    types: [
      // Shown on the business's Google Search and Maps listing. One photo at
      // most; an offer or an event also carries a title and its dates.
      T('Update', 'Update', 'none', 'landscape'),
      T('Offer', 'Offer', 'none', 'landscape', 'offer'),
      T('Event', 'Event', 'none', 'landscape', 'event'),
    ],
  },
  {
    platformId: 'zetpin',
    name: 'ZETPIN',
    label: 'Zetpin',
    chars: PLATFORM_LIMITS.ZETPIN.chars,
    hashtags: PLATFORM_LIMITS.ZETPIN.hashtags,
    linksClickable: true,
    types: [
      // Text alone or up to ten images. A hyperlocal post also carries the
      // place it is about, so it reaches people near it.
      T('Post', 'Post', 'none', 'square'),
      T('Hyperlocal', 'Hyperlocal', 'none', 'square', 'hyperlocal'),
    ],
  },
];

/** The channels the composer can publish to: every channel except inbox-only ones. */
export const PUBLISHABLE_RULES: PlatformRule[] = PLATFORM_RULES.filter((rule) => !rule.inboxOnly);


export function ruleFor(name: string): PlatformRule | undefined {
  return PLATFORM_RULES.find((p) => p.name === name);
}

/**
 * Post types the user may actually pick right now.
 *
 * Every rule's types are currently offerable, so this is a pass-through. It
 * stays as a function because the composer calls it in three places, and a
 * platform that gates a type behind a scope or an app review will need the
 * filter back.
 */
export function availableTypes(rule: PlatformRule): PostTypeRule[] {
  return rule.types;
}

/** Is this type's media requirement satisfied by what's attached? */
export function typeSatisfied(need: MediaNeed, media: { type: 'image' | 'video' }[]): boolean {
  const images = media.filter((m) => m.type === 'image').length;
  const videos = media.filter((m) => m.type === 'video').length;
  switch (need) {
    case 'video': return videos > 0;
    case 'image': return images > 0;
    case 'media': return media.length > 0;
    case 'carousel': return media.length >= 2;
    default: return true;
  }
}

/**
 * The post type a single still (an occasion poster) goes out as: the first
 * type one image satisfies, so a poster is a TikTok photo post rather than a
 * video it is not. Falls back to the first type where none fits.
 */
export function stillTypeFor(name: string): PostTypeRule | undefined {
  const rule = ruleFor(name);
  return rule?.types.find((t) => typeSatisfied(t.needs, [{ type: 'image' }])) ?? rule?.types[0];
}

export function typeRule(name: string, typeId: string): PostTypeRule | undefined {
  return ruleFor(name)?.types.find((t) => t.id === typeId);
}

/** Unicode-safe grapheme count (matches how platforms count). */
export function graphemes(text: string): number {
  if ('Segmenter' in Intl) {
    let n = 0;
    for (const _ of new Intl.Segmenter('en', { granularity: 'grapheme' }).segment(text)) { n++; void _; }
    return n;
  }
  return [...text].length;
}

/**
 * Characters as THIS platform counts them. X weighs its own way (a flat 23 per
 * URL, 2 for emoji, CJK and most non-Latin scripts); Telegram and TikTok count
 * UTF-16 units (a Malayalam letter is often 2–3), so the composer uses the
 * same counter the server checks with; a naive `length` says a tweet fits
 * when X will refuse it.
 */
export function effectiveChars(text: string, rule: PlatformRule): number {
  if (rule.urlWeight) return xWeightedLength(text);
  // Telegram and TikTok count UTF-16 units, as the formatter and the channel do.
  return platformTextLength(rule.name, text);
}

/**
 * Whether the caption is published as an X thread: its text is split into
 * several posts, so the 280 limit is each post's, not the caption's.
 */
export function isThreadOnX(platform: string, types: Iterable<string>): boolean {
  if (platform.toUpperCase() !== 'TWITTER') return false;
  for (const type of types) if (isXThread(type)) return true;
  return false;
}

/** Hashtags as the publish formatter counts them (platform-constants hashtags.ts): every script, never a link's #fragment. */
export function countHashtags(text: string): number {
  return hashtagCount(text);
}

export interface PlatformIssue {
  platform: string;
  /** The platform's name (a brand name, not translated). */
  label: string;
  /** blocking = can't publish; warning = allowed but risky. */
  level: 'block' | 'warn';
  /** i18n key of the message; render it with issueMessage(). */
  messageKey: string;
  /**
   * Interpolation values. Numbers are formatted for the reader's locale, and
   * `typeKey` (a post type's labelKey) is translated into `type`.
   */
  params?: Record<string, string | number>;
}

const PLACEHOLDER = /\{\{\s*([\w-]+)\s*\}\}/g;

/**
 * Template blanks still in a caption (`{{price}}`, `{{booking_link}}`), each
 * once, as written. A starter-pack template is full of them; published as
 * they are, customers would read the braces.
 */
export function unfilledPlaceholders(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(PLACEHOLDER)) {
    if (!out.includes(m[0])) out.push(m[0]);
  }
  return out;
}

/**
 * Validate the composed post against every selected platform + type. This is
 * what turns "publish failed on the worker" into "you can see the problem before
 * you click".
 */
export function validateComposition(input: {
  caption: string;
  platforms: Map<string, Set<string>>;
  media: { type: 'image' | 'video' }[];
}): PlatformIssue[] {
  const { caption, platforms, media } = input;
  const issues: PlatformIssue[] = [];
  const images = media.filter((m) => m.type === 'image').length;
  const videos = media.filter((m) => m.type === 'video').length;
  const tags = countHashtags(caption);

  for (const [name, types] of platforms) {
    const rule = ruleFor(name);
    if (!rule || types.size === 0) continue;

    // Caption length, counted the platform's own way. An X thread has no
    // caption limit: it is split into posts that each fit. A TikTok photo
    // post's caption is its description, which takes 4,000.
    const used = effectiveChars(caption, rule);
    const limit = name === 'TIKTOK' && [...types].every((t) => t === 'Photo') ? TIKTOK_PHOTO_DESC_MAX : rule.chars;
    if (used > limit && !isThreadOnX(name, types)) {
      issues.push({
        platform: name,
        label: rule.label,
        level: 'block',
        messageKey: 'composer.rules.issue.overLimit',
        params: { over: used - limit, limit },
      });
    }

    // Instagram refuses a caption that tags more than 20 accounts.
    if (name === 'INSTAGRAM') {
      const mentions = countMentions(caption);
      if (mentions > INSTAGRAM_MAX_MENTIONS) {
        issues.push({
          platform: name,
          label: rule.label,
          level: 'block',
          messageKey: 'composer.rules.issue.mentionsOver',
          params: { count: mentions, max: INSTAGRAM_MAX_MENTIONS, platform: rule.label },
        });
      }
    }

    // Hashtag caps: Instagram rejects >30, LinkedIn just de-ranks.
    if (rule.hashtags !== null && tags > rule.hashtags) {
      issues.push({
        platform: name,
        label: rule.label,
        level: rule.hashtagsAreSoftCap ? 'warn' : 'block',
        messageKey: rule.hashtagsAreSoftCap ? 'composer.rules.issue.hashtagsSoftCap' : 'composer.rules.issue.hashtagsOver',
        params: { count: tags, max: rule.hashtags, platform: rule.label },
      });
    }

    // Media requirements per selected TYPE.
    for (const t of types) {
      const tr = typeRule(name, t);
      if (!tr) continue;
      const need = tr.needs;
      const typed = (messageKey: string): PlatformIssue =>
        ({ platform: name, label: rule.label, level: 'block', messageKey, params: { typeKey: tr.labelKey } });
      if (need === 'video' && videos === 0) {
        issues.push(typed('composer.rules.issue.needsVideo'));
      } else if (need === 'image' && images === 0) {
        issues.push(typed('composer.rules.issue.needsImage'));
      } else if (need === 'media' && media.length === 0) {
        issues.push(typed('composer.rules.issue.needsMedia'));
      } else if (need === 'carousel' && media.length < 2) {
        issues.push({ platform: name, label: rule.label, level: 'block', messageKey: 'composer.rules.issue.carouselMin' });
      }

      // Instagram: only a carousel carries several items, and at most 20.
      if (name === 'INSTAGRAM') {
        if (need === 'carousel' && media.length > INSTAGRAM_CAROUSEL_MAX) {
          issues.push({ platform: name, label: rule.label, level: 'block', messageKey: 'composer.rules.issue.carouselMax', params: { max: INSTAGRAM_CAROUSEL_MAX } });
        } else if (need !== 'carousel' && media.length > 1) {
          issues.push(typed('composer.rules.issue.carouselNeeded'));
        }
      }

      // Facebook: a Story is one photo or one video; a Reel is one video and
      // nothing else (its "needs a video" is checked above).
      if (name === 'FACEBOOK') {
        if (t === 'Story' && media.length > 1) {
          issues.push({ platform: name, label: rule.label, level: 'block', messageKey: 'composer.rules.issue.maxOne', params: { platform: rule.label } });
        } else if (t === 'Reel' && videos > 0) {
          if (images > 0) issues.push({ platform: name, label: rule.label, level: 'block', messageKey: 'composer.rules.issue.noMix' });
          else if (videos > 1) issues.push({ platform: name, label: rule.label, level: 'block', messageKey: 'composer.rules.issue.maxOneVideo' });
        }
      }

      // LinkedIn: a poll carries no media; an article at most one image,
      // shown as the link's picture.
      if (name === 'LINKEDIN') {
        if (t === 'Poll' && media.length > 0) issues.push(typed('composer.rules.issue.noMedia'));
        else if (t === 'Article' && (videos > 0 || images > 1)) issues.push(typed('composer.rules.issue.oneImageAtMost'));
      }
    }

    // The most media each channel takes; past it the publish would fail or
    // quietly leave files out.
    const block = (messageKey: string, params?: PlatformIssue['params']): void => {
      issues.push({ platform: name, label: rule.label, level: 'block', messageKey, ...(params ? { params } : {}) });
    };
    if (name === 'TWITTER') {
      if (images > 0 && videos > 0) block('composer.rules.issue.noMix');
      else if (videos > 1) block('composer.rules.issue.maxOneVideo');
      else if (images > X_MAX_IMAGES) block('composer.rules.issue.maxImages', { max: X_MAX_IMAGES });
    } else if (name === 'TIKTOK') {
      // A video post or a photo post: with both, TikTok would post the video alone.
      if (images > 0 && videos > 0) block('composer.rules.issue.noMix');
      else if (videos > 1) block('composer.rules.issue.maxOneVideo');
      else if (images > TIKTOK_MAX_PHOTOS) block('composer.rules.issue.maxImages', { max: TIKTOK_MAX_PHOTOS });
    } else if (name === 'FACEBOOK' && !types.has('Story') && !types.has('Reel')) {
      // A Page post is photos or one video: a video sent with photos would
      // drop the photos, and photos before a video would fail.
      if (images > 0 && videos > 0) block('composer.rules.issue.noMix');
      else if (videos > 1) block('composer.rules.issue.maxOneVideo');
    } else if (name === 'LINKEDIN' && !types.has('Poll') && !types.has('Article')) {
      // One video, or up to 20 images; past either, LinkedIn drops files.
      if (images > 0 && videos > 0) block('composer.rules.issue.noMix');
      else if (videos > 1) block('composer.rules.issue.maxOneVideo');
      else if (images > LINKEDIN_MAX_IMAGES) block('composer.rules.issue.maxImages', { max: LINKEDIN_MAX_IMAGES });
    } else if (name === 'ZETPIN') {
      if (videos > 0) block('composer.rules.issue.imagesOnly', { platform: rule.label });
      else if (images > ZETPIN_MAX_IMAGES) block('composer.rules.issue.maxImages', { max: ZETPIN_MAX_IMAGES });
    } else if (SINGLE_MEDIA_PLATFORMS.has(name) && media.length > 1) {
      block('composer.rules.issue.maxOne', { platform: rule.label });
    }

    // Pinterest rejects a pin without a destination link.
    if (rule.requiresLink && !hasLink(caption)) {
      issues.push({ platform: name, label: rule.label, level: 'block', messageKey: 'composer.rules.issue.needsLink', params: { platform: rule.label } });
    }

    // Text-only posts still need SOMETHING.
    if (caption.trim() === '' && media.length === 0) {
      issues.push({ platform: name, label: rule.label, level: 'block', messageKey: 'composer.rules.issue.empty' });
    }

    // A template's blanks never go out as literal braces.
    const blanks = unfilledPlaceholders(caption);
    if (blanks.length > 0) {
      issues.push({ platform: name, label: rule.label, level: 'block', messageKey: 'composer.rules.issue.placeholder', params: { fields: blanks.join(', ') } });
    }
  }

  return issues;
}
